import { useState } from "react";
import type {
  MeetingLocationType,
  SchedulingPage,
  SchedulingPageInput,
} from "@sales-ops-amplify/shared";
import {
  useCreateSchedulingPage,
  useUpdateSchedulingPage,
} from "@/hooks/useMeetings";

export function SchedulingPageModal({
  page,
  onClose,
}: {
  page?: SchedulingPage | null;
  onClose: () => void;
}) {
  const createMut = useCreateSchedulingPage();
  const updateMut = useUpdateSchedulingPage();
  const [name, setName] = useState(page?.name ?? "30-minute meeting");
  const [slug, setSlug] = useState(page?.slug ?? "");
  const [durationMin, setDurationMin] = useState(page?.durationMin ?? 30);
  const [locationType, setLocationType] = useState<MeetingLocationType>(
    page?.locationType ?? "google_meet",
  );
  const [customLocation, setCustomLocation] = useState(
    page?.customLocation ?? "",
  );
  const [description, setDescription] = useState(page?.description ?? "");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const input: SchedulingPageInput = {
      name,
      slug: slug || undefined,
      durationMin,
      locationType,
      customLocation: customLocation || undefined,
      description: description || undefined,
      hostMode: "single",
      active: true,
    };
    try {
      if (page) {
        await updateMut.mutateAsync({ pageId: page.pageId, patch: input });
      } else {
        await createMut.mutateAsync(input);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  const pending = createMut.isPending || updateMut.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-4 rounded-lg border border-border bg-background p-5 shadow-lg"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {page ? "Edit scheduling page" : "New scheduling page"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </div>

        <label className="block text-sm">
          <span className="text-muted-foreground">Name</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>

        <label className="block text-sm">
          <span className="text-muted-foreground">Slug (optional)</span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="30-minute-meeting"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>

        <label className="block text-sm">
          <span className="text-muted-foreground">Duration (minutes)</span>
          <input
            type="number"
            min={5}
            max={240}
            value={durationMin}
            onChange={(e) => setDurationMin(Number(e.target.value) || 30)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>

        <label className="block text-sm">
          <span className="text-muted-foreground">Location</span>
          <select
            value={locationType}
            onChange={(e) =>
              setLocationType(e.target.value as MeetingLocationType)
            }
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          >
            <option value="google_meet">Google Meet</option>
            <option value="custom">Custom URL</option>
            <option value="phone">Phone</option>
            <option value="in_person">In person</option>
          </select>
        </label>

        {locationType === "custom" && (
          <label className="block text-sm">
            <span className="text-muted-foreground">Custom location / URL</span>
            <input
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </label>
        )}

        <label className="block text-sm">
          <span className="text-muted-foreground">Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-60"
          >
            {pending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
